import React, { SetStateAction, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendCompanySampleAdditionalInfoFields,
	FrontendCompanyTestCategory,
	FrontendSample,
	FrontendSampleInstanceAdditionalInfoMapping,
} from "node-library/dist/portal";
import {
	AdditionalInfoFieldType,
	AdditionalInfoType,
	BTGNCustomOption,
	EColiCustomOption,
	FrontendTurnaroundTimeOptions,
	ReceivingCondition,
	TurnaroundTime,
	WeightUnit,
} from "node-library/dist/constants";
import { toBoolean } from "node-library/dist/helpers/misc_helper";
import {
	CommonSwitch,
	StandardRadioGroup,
	CustomTextInput,
	CustomRadioGroup,
	RowDropdown,
	CustomCheckbox,
	CustomDropdown,
} from "../../../common-components/inputs";
import { Box, Card, Divider } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	GreenButton,
	GreyButton,
	OutlinedButton,
} from "../../../common-components/button";

import { ModalWithButtons } from "../../../common-components/modals/modal";
import AdjustWeightModalButton from "./components/adjustWeightModalButton";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";

import {
	CustomDatePicker,
	CustomDateTimePicker,
} from "../../../common-components/datePicker";
import {
	getReceivingConditionTranslationText,
	TATOptionToTurnaroundTime,
	TurnaroundTimeToTATOption,
} from "../../../helpers/sampleHelper";
interface Props {
	orderTestCategory: FrontendCompanyTestCategory;

	sampleName: string;
	lotCode: string;

	sampleMatrix: string;

	selectedSample: FrontendSample | undefined;
	weight: string;
	setWeight: React.Dispatch<React.SetStateAction<string>>;

	weightUnit: WeightUnit;
	setWeightUnit: React.Dispatch<React.SetStateAction<WeightUnit>>;

	turnaroundTime: TurnaroundTime;
	setTurnaroundTime: React.Dispatch<React.SetStateAction<TurnaroundTime>>;

	packageReceivedTime: Date | null;
	setPackageReceivedTime: React.Dispatch<React.SetStateAction<Date | null>>;

	expectedCompletionDate: Date | null;
	setExpectedCompletionDate: React.Dispatch<React.SetStateAction<Date | null>>;

	receivingCondition: ReceivingCondition;
	setReceivingCondition: React.Dispatch<
		React.SetStateAction<ReceivingCondition>
	>;

	isNewSample: boolean;
	isRetest: boolean;
	setIsRetest: React.Dispatch<React.SetStateAction<boolean>>;

	isAdditionalTest: boolean;
	setIsAdditionalTest: React.Dispatch<React.SetStateAction<boolean>>;
	sampleInstanceAdditionalInfos: Array<FrontendSampleInstanceAdditionalInfoMapping>;
	setSampleInstanceAdditionalInfos: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleInstanceAdditionalInfoMapping>>
	>;

	setCurrentPageIndex: (value: number) => void;

	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function StepThreeSampleInstanceInfo(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		orderTestCategory,
		sampleMatrix,
		isNewSample,
		setCurrentPageIndex,

		setUnsavedChanges,
	} = props;

	const [sampleInstanceAdditionalInfos, setSampleInstanceAdditionalInfos] =
		useState<FrontendSampleInstanceAdditionalInfoMapping[]>(
			props.sampleInstanceAdditionalInfos || [],
		);
	const [weight, setWeight] = useState<string>(props.weight || "");

	const [weightUnit, setWeightUnit] = useState<WeightUnit>(
		props.weightUnit || WeightUnit.GRAMS,
	);

	const [packageReceivedTime, setPackageReceivedTime] = useState<Date | null>(
		props.packageReceivedTime
			? new Date(props.packageReceivedTime)
			: new Date(),
	);

	const [expectedCompletionDate, setExpectedCompletionDate] =
		useState<Date | null>(
			props.expectedCompletionDate
				? new Date(props.expectedCompletionDate)
				: null,
		);

	const [receivingCondition, setReceivingCondition] =
		useState<ReceivingCondition>(
			props.receivingCondition || ReceivingCondition.AMBIENT,
		);

	const [isRetest, setIsRetest] = useState<boolean>(props.isRetest);
	const [isAdditionalTest, setIsAdditionalTest] = useState<boolean>(
		props.isAdditionalTest,
	);

	const [TATOption, setTATOption] = useState<FrontendTurnaroundTimeOptions>(
		TurnaroundTimeToTATOption(props.turnaroundTime),
	);

	const weightCardRef = useRef<HTMLDivElement>(null);

	//validations

	const [
		sampleInstanceAdditionalInfosErrorMsg,
		setSampleInstanceAdditionalInfosErrorMsg,
	] = useState<Array<{ id: string; message: string }>>([]);

	const [weightError, setWeightError] = useState<string>("");

	const [packageReceivedTimeError, setPackageReceivedTimeError] =
		useState<string>("");

	const [expectedCompletionDateError, setExpectedCompletionDateError] =
		useState<string>("");

	const mandatoryFields: Array<any> = [
		{
			field: weight,
			errorMsg: weightError,
			setErrorMsg: setWeightError,
		},
		{
			field: packageReceivedTime,
			errorMsg: packageReceivedTimeError,
			setErrorMsg: setPackageReceivedTimeError,
		},
		{
			field: expectedCompletionDate,
			errorMsg: expectedCompletionDateError,
			setErrorMsg: setExpectedCompletionDateError,
		},
	];

	const testCategorySampleInstanceAdditionalInfoFields: Array<React.ReactNode> =
		orderTestCategory.sampleAdditionalInfo
			.filter((info) => {
				return info.infoType === AdditionalInfoType.SAMPLE_INSTANCE;
			})
			.map((info) => {
				const existingInfo = sampleInstanceAdditionalInfos.find((existing) => {
					return info.fieldId === existing.fieldId;
				});
				const value = existingInfo?.value || "";
				const setValueFunction = (value: string | Date | null) => {
					if (value == null) return;
					const updatedInfos: Array<FrontendSampleInstanceAdditionalInfoMapping> =
						sampleInstanceAdditionalInfos.map((existing) =>
							info.fieldId === existing.fieldId
								? { ...existing, value: value }
								: existing,
						);
					if (!existingInfo) {
						updatedInfos.push({
							fieldId: info.fieldId,
							value: value,
							infoType: AdditionalInfoType.SAMPLE_INSTANCE,
							additionalFieldName: info.additionalFieldName,
							fieldType: info.fieldType,
						});
					}
					setSampleInstanceAdditionalInfos(updatedInfos);
					setUnsavedChanges(true);
				};
				switch (info.fieldType) {
					case AdditionalInfoFieldType.TEXT:
						return (
							<CustomTextInput
								id={`instance-${info.additionalFieldName}-input`}
								label={info.additionalFieldName}
								value={value as string}
								setValue={setValueFunction}
								// description={translation("LotCodeDescription")}
								required={info.mandatory}
								errorMsg={
									sampleInstanceAdditionalInfosErrorMsg.find((msg) => {
										return msg.id === info.fieldId;
									})?.message || ""
								}
								setErrorMsg={(value) => {
									const updatedMsgs = sampleInstanceAdditionalInfosErrorMsg.map(
										(existing) =>
											info.fieldId === existing.id
												? { ...existing, message: value }
												: existing,
									);
									if (
										!sampleInstanceAdditionalInfosErrorMsg.find((msg) => {
											return msg.id === info.fieldId;
										})
									) {
										updatedMsgs.push({
											id: info.fieldId,
											message: value,
										});
									}
									setSampleInstanceAdditionalInfosErrorMsg(updatedMsgs);
								}}
							/>
						);
					case AdditionalInfoFieldType.DATE:
						return (
							<div className="modal-datepicker-row">
								<div className="typography-body-01">
									{info.additionalFieldName}
								</div>

								<CustomDatePicker
									selectedDate={new Date(value)}
									setSelectedDate={setValueFunction}
									errorMsg={
										sampleInstanceAdditionalInfosErrorMsg.find((msg) => {
											return msg.id === info.fieldId;
										})?.message || ""
									}
								></CustomDatePicker>
							</div>
						);
				}
			});

	const handleSubmitPackageInfo = () => {
		setWeightError("");
		setPackageReceivedTimeError("");
		setExpectedCompletionDateError("");
		let hasError: boolean = false;
		for (const field of mandatoryFields) {
			field.setErrorMsg("");
			if (!field.field) {
				field.setErrorMsg(translation("MissingInformation"));
				hasError = true;
			}
			if (field.field instanceof Date && isNaN(field.field.getTime())) {
				// Check if date is "Invalid Date"
				field.setErrorMsg(translation("MissingInformation"));
				hasError = true;
			}
		}
		//check mandatory additional fields
		const mandatoryAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
			orderTestCategory.sampleAdditionalInfo.filter((info) => {
				return (
					info.infoType === AdditionalInfoType.SAMPLE_INSTANCE && info.mandatory
				);
			});
		for (const field of mandatoryAdditionalFields) {
			const matchingInfo = sampleInstanceAdditionalInfos.find((info) => {
				return field.fieldId === info.fieldId;
			});
			if (!matchingInfo || !matchingInfo.value) {
				//set error
				hasError = true;
				const updatedMsgs = sampleInstanceAdditionalInfosErrorMsg.map(
					(existing) =>
						field.fieldId === existing.id
							? { ...existing, message: translation("MissingInformation") }
							: existing,
				);
				if (
					!sampleInstanceAdditionalInfosErrorMsg.find((msg) => {
						return msg.id === field.fieldId;
					})
				) {
					updatedMsgs.push({
						id: field.fieldId,
						message: translation("MissingInformation"),
					});
				}
				setSampleInstanceAdditionalInfosErrorMsg(updatedMsgs);
				break;
			}
		}

		if (hasError) {
			return;
		}
		props.setSampleInstanceAdditionalInfos(sampleInstanceAdditionalInfos);

		props.setWeight(weight);
		props.setWeightUnit(weightUnit);
		props.setPackageReceivedTime(packageReceivedTime);
		props.setExpectedCompletionDate(expectedCompletionDate);
		props.setReceivingCondition(receivingCondition);
		props.setIsRetest(isRetest);
		props.setIsAdditionalTest(isAdditionalTest);
		props.setTurnaroundTime(TATOptionToTurnaroundTime(TATOption));

		setCurrentPageIndex(3);
	};

	const sampleInfoCard: React.ReactNode = (
		<Card
			className="common-card card-border-radius-14p"
			style={{ height: "100%", margin: 0 }}
		>
			<div className="new-sample-info-card-container-grid">
				<div className="sample-info-card-content">
					<div className="typography-heading-03">
						{translation("SampleDetails")}
					</div>
					<div className="choose-package-sample-info-card">
						<div className="overflow-ellipsis-text typography-body-01">{`${translation(
							"Name",
						)}: ${props.sampleName}`}</div>
						<Divider
							className="choose-package-sample-info-first-divider"
							orientation="vertical"
							flexItem
						/>

						<div className="overflow-ellipsis-text typography-body-01">{`${translation(
							"Lot",
						)}: ${props.lotCode}`}</div>
						<Divider
							className="choose-package-sample-info-second-divider"
							orientation="vertical"
							flexItem
						/>

						<div className="choose-package-full-width-column typography-body-01">{`${translation(
							"Matrix",
						)}: ${sampleMatrix}`}</div>
					</div>
				</div>
			</div>
		</Card>
	);

	//sample additional info starts
	const sampleInstanceAdditionalInfoInput: React.ReactNode = (
		<div className="sample-info-name-lotcode-row">
			{testCategorySampleInstanceAdditionalInfoFields}
		</div>
	);
	//sample additional info ends

	let weightUnitOptions: Array<{ value: WeightUnit; title: string }> =
		Object.values(WeightUnit).map((value) => {
			return { value: value, title: translation(value) };
		});

	const weightSelector: React.ReactNode = (
		<CustomTextInput
			id="new-sample-weight-input"
			label={translation("Weight")}
			value={weight}
			setValue={(value) => {
				setWeight(value);
				setUnsavedChanges(true);
			}}
			placeholder={"0"}
			required
			errorMsg={weightError}
			setErrorMsg={setWeightError}
		/>
	);

	const weightUnitSelector: React.ReactNode = (
		<CustomDropdown
			id="weight-unit-dropdown"
			label={""}
			options={weightUnitOptions}
			value={weightUnit}
			setValue={(value) => {
				setWeightUnit(value);
				setUnsavedChanges(true);
			}}
		></CustomDropdown>
	);

	const receiveSampleTimePicker: React.ReactNode = (
		<div className="modal-datepicker-row">
			<div className="typography-body-01">{translation("ReceivedTime")}</div>

			<CustomDateTimePicker
				selectedDate={packageReceivedTime}
				setSelectedDate={setPackageReceivedTime}
				errorMsg={packageReceivedTimeError}
			></CustomDateTimePicker>
		</div>
	);
	const expectedCompletionDatePicker: React.ReactNode = (
		<div className="modal-datepicker-row">
			<div className="typography-body-01">{translation("DueDate")}</div>
			<CustomDatePicker
				selectedDate={expectedCompletionDate}
				setSelectedDate={setExpectedCompletionDate}
				errorMsg={expectedCompletionDateError}
			/>
		</div>
	);
	const receivingConditionSelector: React.ReactNode = (
		<CustomDropdown
			id="receiving-condition-dropdown"
			label={translation("ReceivingCondition")}
			options={Object.values(ReceivingCondition).map((value) => {
				return {
					value: value,
					title: getReceivingConditionTranslationText(value, translation),
				};
			})}
			value={receivingCondition}
			setValue={(value) => {
				setReceivingCondition(value);
				setUnsavedChanges(true);
			}}
		></CustomDropdown>
	);

	// const anyTestsPerformed: boolean =
	// 	performedBasicTests || performedMicroTests ? true : false;

	const retestSampleSwitch: React.ReactNode = (
		<div className="common-card-header">
			<div>
				<label className="typography-body-01">
					{translation("RetestSample")}
				</label>
				{/* <div
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					className="typography-caption"
				>
					{`${translation(
						"TestsPerformed"
					)}: ${performedBasicTests}, ${performedMicroTests}`}
				</div> */}
			</div>

			<CommonSwitch
				id={"retest-sample-switch"}
				checked={isRetest}
				setChecked={(value) => {
					setIsRetest(value);
					setUnsavedChanges(true);
				}}
			/>
		</div>
	);

	const additionalTestSampleSwitch: React.ReactNode = (
		<div className="common-card-header">
			<div>
				<label className="typography-body-01">
					{translation("Additionaltest")}
				</label>
				{/* <div
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					className="typography-caption"
				>
					{`${translation(
						"TestsPerformed"
					)}: ${performedBasicTests}, ${performedMicroTests}`}
				</div> */}
			</div>

			<CommonSwitch
				id={"additional-test-sample-switch"}
				checked={isAdditionalTest}
				setChecked={(value) => {
					setIsAdditionalTest(value);
					setUnsavedChanges(true);
				}}
			/>
		</div>
	);

	const retestSampleSwitchContainer: React.ReactNode =
		props.isNewSample ? null : (
			<Card className="sample-info-collapsible-card card-medium-padding">
				{retestSampleSwitch}
			</Card>
		);

	const additionalTestSampleSwitchContainer: React.ReactNode = (
		<Card className="sample-info-collapsible-card card-medium-padding">
			{additionalTestSampleSwitch}
		</Card>
	);
	const TATListOptions = Object.values(FrontendTurnaroundTimeOptions);

	function changeTAT(TAT: FrontendTurnaroundTimeOptions) {
		setTATOption(TAT);
		setUnsavedChanges(true);
	}

	const turnaroundTimeDropdown: React.ReactNode = (
		<RowDropdown
			id="turnaroundTime-dropdown"
			label={translation("SelectTurnaroundTime")}
			labelClassName="typography-body-01"
			// description={translation("SelectTurnaroundTimeDescription")}
			// labelDescriptionColor={SemanticColor.INFO_MEDIUM}
			options={TATListOptions.map((value) => {
				return {
					value: value,
					title: translation(value),
				};
			})}
			value={TATOption}
			setValue={changeTAT}
		></RowDropdown>
	);

	const sampleWeightCard: React.ReactNode = (
		<Card
			className="common-card card-border-radius-14p"
			style={{ height: "100%", margin: 0 }}
		>
			<div className="sample-info-card-content" ref={weightCardRef}>
				<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
					<div className="typography-heading-03">{`${translation(
						"SampleInstanceInformation",
					)}`}</div>
				</div>
				{sampleInstanceAdditionalInfoInput}
				<div className="new-sample-weight-and-unit-container">
					{weightSelector}
					{weightUnitSelector}
				</div>
				<div
					style={{
						marginTop: "10px",
						display: "flex",
						flexDirection: "column",
						gap: "5px",
					}}
				>
					{receiveSampleTimePicker}
					<div> {turnaroundTimeDropdown}</div>

					{expectedCompletionDatePicker}
				</div>

				<div>{receivingConditionSelector}</div>
				<div className="sample-info-collapsible-card-container">
					{retestSampleSwitchContainer}
					{additionalTestSampleSwitchContainer}
				</div>
			</div>
		</Card>
	);

	const stepThreeSampleInstanceInfoComponent: JSX.Element = (
		<div>
			{sampleInfoCard}

			<div style={{ marginTop: "30px" }}>
				{sampleWeightCard}

				<div className="add-sample-form-button-panel">
					<GreyButton
						id="back-button"
						children={translation("Back")}
						onClick={() => setCurrentPageIndex(1)}
					></GreyButton>
					<GreenButton
						id="submit-package-info-button"
						children={translation("Next")}
						type="submit"
						onClick={() => handleSubmitPackageInfo()}
					></GreenButton>
				</div>
			</div>
		</div>
	);

	return (
		<div style={{ margin: "0 auto" }}>
			{stepThreeSampleInstanceInfoComponent}
		</div>
	);
}
